import { createFileRoute, useSearch } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { useState } from "react";
import { Languages, Target, Zap } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { DEMO_ROLES, demoRequestSchema } from "@/lib/demo/schema";
import { toast } from "sonner";
import { track, trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/request-a-demo")({
  validateSearch: (s: Record<string, unknown>): { plan?: string; source?: string; report?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_content?: string; utm_term?: string } => ({
    plan: typeof s.plan === "string" ? s.plan : undefined,
    source: typeof s.source === "string" ? s.source.slice(0, 64) : undefined,
    report: typeof s.report === "string" ? s.report.slice(0, 160) : undefined,
    utm_source: typeof s.utm_source === "string" ? s.utm_source : undefined,
    utm_medium: typeof s.utm_medium === "string" ? s.utm_medium : undefined,
    utm_campaign: typeof s.utm_campaign === "string" ? s.utm_campaign : undefined,
    utm_content: typeof s.utm_content === "string" ? s.utm_content : undefined,
    utm_term: typeof s.utm_term === "string" ? s.utm_term : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Request a Demo - Media Monitoring Platform | Perceptica" },
      { name: "description", content: "See Perceptica's media monitoring platform live. Walk through media listening, media intelligence and media analysis built for your brand. Book a slot." },
      { property: "og:title", content: "Request a Demo - Media Monitoring Platform | Perceptica" },
      { property: "og:description", content: "See Perceptica's media monitoring platform live. Walk through media listening, media intelligence and media analysis built for your brand. Book a slot." },

      ...ogImageMeta("en", "/request-a-demo", "Request a Perceptica demo"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Request a Demo - Media Monitoring Platform | Perceptica" },
      { name: "twitter:description", content: "See Perceptica's media monitoring platform live. Walk through media listening, media intelligence and media analysis built for your brand. Book a slot." },
      { property: "og:url", content: "https://perceptica.com/request-a-demo" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/request-a-demo" }),
  }),
  component: RequestDemo,
});

function RequestDemo() {
  const search = useSearch({ from: "/request-a-demo" });
  const { plan } = search;
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      company: String(fd.get("company") ?? ""),
      role: String(fd.get("role") ?? ""),
      useCase: String(fd.get("useCase") ?? ""),
      consent: fd.get("consent") === "on",
      website: String(fd.get("website") ?? ""),
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      formName: search.source ? `request-a-demo:${search.source}${search.report ? `:${search.report}` : ""}`.slice(0, 160) : "request-a-demo",
      utm_source: search.utm_source || "",
      utm_medium: search.utm_medium || "",
      utm_campaign: search.utm_campaign || "",
      utm_content: search.utm_content || "",
      utm_term: search.utm_term || "",
    };
    const parsed = demoRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(flat)) if (v?.[0]) next[k] = v[0];
      setErrors(next);
      trackFormFailure({ form_name: "request-a-demo", reason: "validation_error", error_code: Object.keys(next).join(","), locale: "en" });
      return;
    }
    setSubmitting(true);
    try {
      // Also submit directly to HubSpot Forms API so HubSpot workflows/emails
      // are triggered for this form. Fire-and-forget - failures here must not
      // block the primary /api/public/demo-request submission.
      const hsResult = await fetch(
        "https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/5705333c-1b38-4047-a40e-a5da890b98ed",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { objectTypeId: "0-1", name: "email", value: parsed.data.email },
              { objectTypeId: "0-1", name: "fullname", value: parsed.data.name },
              { objectTypeId: "0-1", name: "company", value: parsed.data.company },
              { objectTypeId: "0-1", name: "jobrole", value: parsed.data.role },
              ...(parsed.data.useCase
                ? [{ objectTypeId: "0-1", name: "message", value: parsed.data.useCase }]
                : []),
            ],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
            legalConsentOptions: {
              consent: {
                consentToProcess: true,
                text: "I agree to be contacted by the Perceptica team.",
              },
            },
          }),
        },
      )
        .then((r) => ({ ok: r.ok, reason: r.ok ? "client_submitted" : `hubspot_http_${r.status}` }))
        .catch((err) => {
          console.error("HubSpot demo form submit failed", err);
          return { ok: false, reason: "hubspot_network_error" };
        });

      const res = await fetch("/api/public/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...parsed.data, hubspotOk: hsResult.ok, hubspotReason: hsResult.reason }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        message?: string;
      };
      if (!res.ok || !data.ok) {
        if (res.status === 429) {
          setServerError("Too many requests right now. Please try again in a minute.");
        } else if (res.status >= 500) {
          setServerError(
            "We couldn't submit your request - our server hit an error. Please try again, or email sales@perceptica.com.",
          );
        } else {
          setServerError(data.error ?? data.message ?? "Something went wrong. Please try again.");
        }
        toast.error("We couldn't submit your demo request. Please try again.");
        trackFormFailure({ form_name: "request-a-demo", reason: failureReasonForStatus(res.status), error_code: res.status, locale: "en" });
        return;
      }
      if (!hsResult.ok) {
        setServerError("We couldn't send your request. Please try again or email sales@perceptica.com.");
        toast.error("We couldn't send your request. Please try again or email sales@perceptica.com.");
        trackFormFailure({ form_name: "request-a-demo", reason: "provider_error", error_code: hsResult.reason, locale: "en" });
        return;
      }
      setSubmitted(true);
      toast.success("Demo request sent. Most requests receive a reply from us within one business day.");
      trackFormSubmit("submit_request_demo", { form_name: "request-a-demo", locale: "en" });
    } catch {
      setServerError("Network error - please check your connection and try again.");
      trackFormFailure({ form_name: "request-a-demo", reason: "network_error", locale: "en" });
      toast.error("Network error - please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1100px] gap-16 px-6 lg:grid-cols-[1fr_1.1fr] lg:px-10">
          <div>
            <span className="inline-flex items-center rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Book a demo
            </span>
            <h1 className="font-display mt-5 text-[clamp(2rem,3.6vw,3rem)] font-medium leading-[1.05] text-foreground">
              See Perceptica with your own brand data.
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-foreground/75">
              A 30-minute walkthrough focused on your brand, competitors and the markets you actually operate in.
            </p>
            <ul className="mt-9 space-y-4">
              {[
                { icon: Languages, label: "Native sentiment in your languages" },
                { icon: Target, label: "Competitor & narrative analysis" },
                { icon: Zap, label: "Tailored setup, transparent pricing" },
              ].map((item) => (
                <li key={item.label} className="flex items-center gap-3 text-[14px] text-foreground/85">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-accent/10 text-accent">
                    <item.icon className="h-4 w-4" />
                  </span>
                  {item.label}
                </li>
              ))}
            </ul>
            <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Most requests receive a reply from us within one business day.
            </p>
          </div>

          <form name="request-a-demo" id="request-a-demo" data-hs-do-not-collect="true"
            className="rounded-2xl border border-border/60 bg-background p-8 shadow-[var(--shadow-soft)]"
            onSubmit={handleSubmit}
            noValidate
          >
            {submitted ? (
              <div className="py-10 text-center">
                <p className="text-gradient-brand font-display text-2xl">Thank you.</p>
                <p className="mt-3 text-[14px] text-foreground/75">
                  We've received your request and will be in touch shortly.
                </p>
              </div>
            ) : (
              <>
                {plan && (
                  <p className="mb-5 inline-flex items-center rounded-full bg-accent/10 px-3 py-1 text-[12px] font-medium text-accent">
                    Plan: {plan}
                  </p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" name="name" required error={errors.name} />
                  <Field label="Work email" name="email" type="email" required error={errors.email} />
                  <Field label="Company" name="company" required error={errors.company} />
                  <SelectField label="Role" name="role" options={[...DEMO_ROLES]} error={errors.role} />
                </div>
                <div className="mt-4">
                  <Field label="What are you hoping to track?" name="useCase" textarea error={errors.useCase} />
                </div>

                {/* Honeypot - must remain empty */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute h-0 w-0 overflow-hidden opacity-0"
                />

                <label className="mt-5 flex items-start gap-2 text-[12.5px] text-foreground/75">
                  <input
                    type="checkbox"
                    name="consent"
                    required
                    className="mt-0.5 h-4 w-4 rounded border-border accent-foreground"
                  />
                  <span>
                    I agree to be contacted by the Perceptica team. See our{" "}
                    <a href="/privacy-policy" className="underline underline-offset-2">privacy policy</a>.
                  </span>
                </label>
                {errors.consent && <p className="mt-1 text-[11.5px] text-destructive">{errors.consent}</p>}

                {serverError && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3"
                  >
                    <p className="text-[12.5px] font-medium text-destructive">
                      We couldn't submit your request
                    </p>
                    <p className="mt-1 text-[12.5px] text-destructive/90">{serverError}</p>
                  </div>
                )}

                <Button type="submit" size="lg" className="mt-6 w-full" disabled={submitting}>
                  {submitting ? "Sending…" : "Request demo"}
                </Button>
                <p className="mt-4 text-[11px] text-muted-foreground">
                  Most requests receive a reply from us within one business day.
                </p>
              </>
            )}
          </form>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  textarea,
  error,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  textarea?: boolean;
  error?: string;
}) {
  const className =
    "w-full rounded-md border bg-background px-3 py-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 " +
    (error
      ? "border-destructive/60 focus:border-destructive focus:ring-destructive/20"
      : "border-border/60 focus:border-accent focus:ring-accent/20");
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-medium text-foreground/80">{label}{required && <span className="text-accent"> *</span>}</span>
      {textarea ? (
        <textarea name={name} required={required} rows={4} className={className} />
      ) : (
        <input name={name} type={type} required={required} className={className} />
      )}
      {error && <span className="mt-1 block text-[11.5px] text-destructive">{error}</span>}
    </label>
  );
}

function SelectField({
  label,
  name,
  required,
  options,
  error,
}: {
  label: string;
  name: string;
  required?: boolean;
  options: string[];
  error?: string;
}) {
  const className =
    "w-full rounded-md border bg-background px-3 py-2.5 text-[13.5px] text-foreground focus:outline-none focus:ring-2 " +
    (error
      ? "border-destructive/60 focus:border-destructive focus:ring-destructive/20"
      : "border-border/60 focus:border-accent focus:ring-accent/20");
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-medium text-foreground/80">
        {label}
        {required && <span className="text-accent"> *</span>}
      </span>
      <select name={name} required={required} className={className} defaultValue="">
        <option value="" disabled>
          Select…
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {error && <span className="mt-1 block text-[11.5px] text-destructive">{error}</span>}
    </label>
  );
}
